import { AlertBanner } from '@/components/common/AlertBanner'
import type { FamilyMember } from '@/components/order/OrderTypes'
import { consentRequiresInteraction } from '@/config/uiConfig'
import type { IncidentalFindingsSelection } from '@/types/RequisitionFormTypes'
import {
  COMPARATOR_INCIDENTAL_LOCKED_NOTICE,
  probandIncidentalFindingsLocksComparator,
} from '@/utils/comparatorIncidentalFindings'
import { InfoCircle } from 'flowbite-react-icons/outline'

import type { ComparatorParentConsentConfigSlice } from './comparatorParentConsentConfig'
import { ConsentAccordionGroup } from './ConsentAccordionGroup'
import { CONSENT_REQUIRED_INTERACTION_ERROR } from './ProbandConsentSections'

interface ComparatorParentConsentSectionsProps {
  configs: ComparatorParentConsentConfigSlice
  data: FamilyMember
  onIncidentalChange: (value: IncidentalFindingsSelection) => void
  onNyRetentionChange: (value: boolean) => void
  onResearchContactChange: (value: boolean) => void
  /** Proband opted out: comparator incidental findings is locked to opt-out (GEN-3484). */
  probandIncidentalFindingsSelection?: IncidentalFindingsSelection | null
  /**
   * Post-submit lock: render all consent toggles read-only (e.g. an already-submitted comparator).
   * Newly added comparators stay editable so consents can be captured before the first send.
   */
  readOnly?: boolean
  /**
   * When true, surface inline validation errors on any `requireInteraction`
   * section that is still unanswered (e.g. after a blocked submit). Defaults to
   * false so a pristine row doesn't render pre-emptive errors.
   */
  showValidationErrors?: boolean
}

/** Tri-state for a boolean opt-in consent: `null` when unset/unanswered. */
function booleanSelectionTriState(value: boolean | null | undefined): boolean | null {
  return value === true ? true : value === false ? false : null
}

export function ComparatorParentConsentSections({
  configs,
  data,
  onIncidentalChange,
  onNyRetentionChange,
  onResearchContactChange,
  probandIncidentalFindingsSelection = null,
  readOnly = false,
  showValidationErrors = false,
}: ComparatorParentConsentSectionsProps) {
  // GEN-3484: a proband opt-out forces every comparator's incidental findings to
  // opt-out and locks the toggle. The post-submit `readOnly` lock disables all toggles.
  const incidentalLockedToOptOut = probandIncidentalFindingsLocksComparator(probandIncidentalFindingsSelection)
  const incidentalOptIn = incidentalLockedToOptOut ? false : data.incidentalFindingsSelection === 'opt-in'
  // Tri-state for the radio mode: a locked comparator is an explicit opt-out
  // (false); otherwise map the stored selection, keeping `null` when unanswered
  // so a `requireInteraction` row renders with neither radio selected.
  const incidentalSelection: boolean | null = incidentalLockedToOptOut
    ? false
    : data.incidentalFindingsSelection === 'opt-in'
      ? true
      : data.incidentalFindingsSelection === 'opt-out'
        ? false
        : null
  const researchSelection = booleanSelectionTriState(data.researchContactConsent)
  const nySelection = booleanSelectionTriState(data.newYorkSampleRetentionOptIn)
  const sectionError = (requireInteraction: boolean, selection: boolean | null): string | undefined =>
    showValidationErrors && requireInteraction && selection === null
      ? CONSENT_REQUIRED_INTERACTION_ERROR
      : undefined
  return (
    <ConsentAccordionGroup
      configs={configs}
      incidentalOptIn={incidentalOptIn}
      researchOptIn={data.researchContactConsent === true}
      nyOptIn={data.newYorkSampleRetentionOptIn === true}
      incidentalSelection={incidentalSelection}
      researchSelection={researchSelection}
      nySelection={nySelection}
      incidentalError={sectionError(consentRequiresInteraction(configs.incidental), incidentalSelection)}
      researchError={sectionError(consentRequiresInteraction(configs.research), researchSelection)}
      nyError={sectionError(consentRequiresInteraction(configs.ny), nySelection)}
      onIncidentalChange={(optIn) => onIncidentalChange(optIn ? 'opt-in' : 'opt-out')}
      onResearchChange={onResearchContactChange}
      onNyChange={onNyRetentionChange}
      disabled={readOnly}
      incidentalDisabled={readOnly || incidentalLockedToOptOut}
      incidentalNotice={
        incidentalLockedToOptOut ? (
          <AlertBanner
            variant="info"
            leftIcon={<InfoCircle className="h-4 w-4 shrink-0 text-text-fg-info" aria-hidden />}
          >
            {COMPARATOR_INCIDENTAL_LOCKED_NOTICE}
          </AlertBanner>
        ) : null
      }
    />
  )
}
